import React, {useEffect, useMemo, useState} from 'react';
import {Box, Stack, Typography} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import SaveIcon from '@mui/icons-material/Save';
import SearchIcon from '@mui/icons-material/Search';
import {t} from 'i18next';
import './QueryHeader.scss';
import {SessionSearch} from '../../types/SessionSearch';
import ArrButton from '../../../common/arr-button/ArrButton';
import {hasAnyCriteriaAdded} from '../../utilities/utilities';
import ArrSingleSelectDropdown from '../../../common/arr-select-dropdown/ArrSingleSelectDropdown';
import {ISelectItem} from '../../../../shared/types';
import ArrLoadingSpinner from '../../../common/arr-loading-spinner/ArrLoadingSpinner';
import {IIndexDropdownResponse} from '../../../../api/types/searchType.type';
import {ToastType} from '../../../../shared/constants';
import ArrToastNotification from '../../../common/arr-toast-notification/ArrToastNotification';
import {useGetIndexDropdown} from '../../../../hooks/search/useSearch';
export interface QueryHeaderProps {
  item: SessionSearch;
  isSearchMode?: boolean;
  onCopy?: (item: SessionSearch) => void;
  onSave?: (
    item: SessionSearch,
    selectedIndexDropdown: IIndexDropdownResponse | undefined
  ) => void;
  onSearch?: (
    item: SessionSearch,
    selectedIndexDropdown: IIndexDropdownResponse | undefined
  ) => void;
  onModify?: (item: SessionSearch) => void;
  onIndexChange?: (
    item: SessionSearch,
    selectedIndexDropdown: IIndexDropdownResponse
  ) => void;
}

const QUERY_TITLE_VISIBILITY_MAX_LENGTH = 80;

const QueryHeader: React.FC<QueryHeaderProps> = ({
  item,
  onCopy,
  onSave,
  onSearch,
  onModify,
  onIndexChange,
}) => {
  const hasSearchBeenExecuted = !!(item.lastRuntime || item.advancedSearchId);
  const [indexDropdown, setIndexDropdown] = useState<IIndexDropdownResponse[]>(
    []
  );
  const {
    data: indexDropdownResponse,
    isError,
    error,
    isLoading: indexDropdownLoading,
  } = useGetIndexDropdown();

  const handleCopyButtonClick = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    onCopy?.(item);
  };

  const handleCopyButtonKeyDown = (e: React.KeyboardEvent<HTMLElement>) => {
    if (e.key === 'Enter' || e.code === 'Enter') {
      e.stopPropagation();
      onCopy?.(item);
    }
  };

  const getSelectDropdownData = () => {
    return indexDropdownResponse?.find(
      (data) => data.indexID === selectedDataset?.id
    );
  };

  const handleSaveButtonClick = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    onSave?.(item, getSelectDropdownData());
  };

  const handleSaveButtonKeyDown = (e: React.KeyboardEvent<HTMLElement>) => {
    if (e.key === 'Enter' || e.code === 'Enter') {
      e.stopPropagation();
      onSave?.(item, getSelectDropdownData());
    }
  };
  const [showToast, setShowToast] = useState<boolean>(false);
  const [toastMessage, setToastMessage] = useState<string>('');
  const [toastStatus, setToastStatus] = useState<ToastType[]>([]);

  const triggerToast = (message: string, status: ToastType[]) => {
    setToastMessage(message);
    setToastStatus(status);
    setShowToast(true);
  };

  useEffect(() => {
    if (indexDropdownLoading || !indexDropdownResponse) {
      return;
    }
    if (isError) {
      setIndexDropdown([]);
      const errorMessage =
        (error as any)?.detail ||
        (error as any)?.message ||
        t('ingestions.indexManagement.viewCustomIndex.viewIndexErrorMsg');
      triggerToast(errorMessage, [ToastType.Error]);
      return;
    }

    setIndexDropdown(indexDropdownResponse);
  }, [indexDropdownResponse, indexDropdownLoading]);

  const dropdownItems: ISelectItem[] = useMemo(
    () =>
      (indexDropdown ?? []).map((d) => ({
        id: d.indexID ?? 0,
        value: d.indexName,
        label: d.indexName,
      })),
    [indexDropdown]
  );

  const [selectedDataset, setSelectedDataset] = useState<ISelectItem | null>(
    dropdownItems[0]
  );

  useEffect(() => {
    if (dropdownItems.length === 0 || selectedDataset) {
      return;
    }

    const selectedIndex =
      dropdownItems.find(
        (dropdownItem) => Number(dropdownItem.id) === Number(item.indexId)
      ) ?? dropdownItems[0];

    setSelectedDataset(selectedIndex);
    const selectedIndexDropdown = indexDropdown.find(
      (index) => Number(index.indexID) === Number(selectedIndex.id)
    );
    if (selectedIndexDropdown) {
      onIndexChange?.(
        {...item, indexId: Number(selectedIndex.id)},
        selectedIndexDropdown
      );
    }
  }, [dropdownItems, item, onIndexChange, selectedDataset]);

  const handleSearchButtonClick = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    onSearch?.(item, getSelectDropdownData());
  };

  const handleSearchButtonKeyDown = (e: React.KeyboardEvent<HTMLElement>) => {
    if (e.key === 'Enter' || e.code === 'Enter') {
      e.stopPropagation();
      onSearch?.(item, getSelectDropdownData());
    }
  };

  const handleModifyButtonClick = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    onModify?.(item);
  };

  const handleModifyButtonKeyDown = (e: React.KeyboardEvent<HTMLElement>) => {
    if (e.key === 'Enter' || e.code === 'Enter') {
      e.stopPropagation();
      onModify?.(item);
    }
  };

  const handleDatasetChange = (selectedItem: ISelectItem | null) => {
    setSelectedDataset(selectedItem);
    if (selectedItem && Number(item.indexId) !== Number(selectedItem.id)) {
      const selectedIndexDropdown = indexDropdown.find(
        (index) => Number(index.indexID) === Number(selectedItem.id)
      );
      if (selectedIndexDropdown) {
        onIndexChange?.(
          {...item, indexId: Number(selectedItem.id)},
          selectedIndexDropdown
        );
      }
    }
  };

  const renderSearchButton = () => {
    let component = <></>;

    if (!hasSearchBeenExecuted) {
      component = (
        <ArrButton
          startIcon={<SearchIcon />}
          size="small"
          onClick={handleSearchButtonClick}
          onKeyDown={handleSearchButtonKeyDown}
          isAriaDisabled={!hasAnyCriteriaAdded(item)}
          className={`button-search ${hasAnyCriteriaAdded(item) ? '' : 'btn-disabled'}`}
          dataTestId="button-search"
        >
          {t('sessionSearch.queryHeader.searchButton')}
        </ArrButton>
      );
    }

    return component;
  };

  const renderModifyButton = () => {
    let component = <></>;

    if (hasSearchBeenExecuted) {
      component = (
        <ArrButton
          size="small"
          onClick={handleModifyButtonClick}
          onKeyDown={handleModifyButtonKeyDown}
          className="button-modify"
          dataTestId="button-modify"
        >
          {t('sessionSearch.queryHeader.modifyButton')}
        </ArrButton>
      );
    }

    return component;
  };

  return (
    <Box className="session-query-header-wrapper">
      <Box className="session-query-header-title">
        <Typography
          className="typo-text subhead3_size query-header-text"
          title={item.name}
          data-testid={`query-header-name-${item.id}`}
          tabIndex={0}
        >
          {item.name.length > QUERY_TITLE_VISIBILITY_MAX_LENGTH
            ? `${item.name.substring(0, QUERY_TITLE_VISIBILITY_MAX_LENGTH)}...`
            : item.name}
        </Typography>
      </Box>
      <Stack
        direction={'row'}
        spacing={1}
        className="session-search-query-header"
      >
        <ArrSingleSelectDropdown
          sizeVariant="small"
          className="dropdown-select-index"
          dropdownLabel={t('sessionSearch.queryHeader.indexDropdown')}
          selectedValues={selectedDataset}
          dropdownItems={dropdownItems}
          onSelectionChange={handleDatasetChange}
          dropdownItemKey="id"
          dropdownItemDisplay="label"
          searchable={true}
          aria-label={t('sessionSearch.queryHeader.indexDropdown')}
          dataTestId="asd-query-index-dropdown"
        />
        <Box>
          <ArrButton
            startIcon={<ContentCopyIcon />}
            size="small"
            variant="text"
            onClick={handleCopyButtonClick}
            onKeyDown={handleCopyButtonKeyDown}
            isAriaDisabled={!hasAnyCriteriaAdded(item)}
            className={`button-copy ${hasAnyCriteriaAdded(item) ? '' : 'btn-disabled'}`}
            dataTestId="button-copy"
          >
            {t('sessionSearch.queryHeader.copyButton')}
          </ArrButton>

          <ArrButton
            startIcon={<SaveIcon />}
            size="small"
            onClick={handleSaveButtonClick}
            onKeyDown={handleSaveButtonKeyDown}
            isAriaDisabled={!hasAnyCriteriaAdded(item)}
            className={`button-save ${hasAnyCriteriaAdded(item) ? '' : 'btn-disabled'}`}
            dataTestId="button-save"
          >
            {t('sessionSearch.queryHeader.saveButton')}
          </ArrButton>

          {renderSearchButton()}

          {renderModifyButton()}
        </Box>
      </Stack>
      {indexDropdownLoading && <ArrLoadingSpinner isOpen overlay />}
      {showToast && (
        <ArrToastNotification
          message={toastMessage}
          status={toastStatus}
          onClose={() => setShowToast(false)}
        />
      )}
    </Box>
  );
};

export default QueryHeader;
